import React from 'react';
import ReactDOM from 'react-dom';
import firebase from 'firebase';
import { ThemeProvider } from 'styled-components';
import { BrowserRouter as Router } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider as ReduxProvider } from 'react-redux';
import { store } from './redux';
import Theme from './theme';
import App from './App';
import './index.css';
import { persistor } from './redux/store';
import { Loader } from './components/atoms';
import { initializeSegment } from './segment';
import { IUserData } from './types';
import { initializeObservability, RouteTracker } from './utils/observability';
import initializeAmplitude, { identifyAmplitudeUser } from './utils/integrations/amplitude';

declare global {
    interface Window {
        recaptchaVerifier: firebase.auth.RecaptchaVerifier;
        confirmationResult: firebase.auth.ConfirmationResult | null;
        analytics: any;
        plotline: (...args: (unknown | Record<string, unknown>)[]) => void;
        _plotQueue?: any[];
        dataLayer?: any[];
        fcWidget: {
            init: (config: any) => void;
            open: () => void;
            destroy: () => void;
            isLoaded: () => boolean;
            on: (a: any, b: any) => void;
            user: IUserData;
            // add more methods and properties as needed
        };
        grecaptcha: {
            enterprise: {
                execute: (siteKey: string, options: { action: string }) => Promise<string>;
                ready: (callback: () => void) => void;
            };
        };
        tidioChatApi?: {
            setVisitorData: (data: { distinct_id?: string; email?: string; name?: string; phone?: string }) => void;
            setContactProperties: (properties: Record<string, string | number | boolean>) => void;
            destroy?: () => void;
            open?: () => void;
            show?: () => void;
            messageFromVisitor?: (message: string) => void;
        };
        amplitude?: {
            init: (apiKey: string, userId?: string, options?: Record<string, unknown>) => void;
            add: (plugin: any) => void;
            identify: (identifyEvent: any) => any;
            Identify: new () => {
                set: (key: string, value: unknown) => any;
            };
            getUserProperties?: () => Record<string, unknown>;
            getInstance?: () => any;
            userProperties?: Record<string, unknown>;
            setUserProperties?: (properties: Record<string, unknown>) => void;
        };
        engagement?: {
            plugin: (options?: { userProperties?: Record<string, unknown> }) => any;
            init: (apiKey: string) => void;
            addCallback?: (key: string, callback: () => void) => void;
            on?: (event: string, callback: () => void) => void;
        };
        sessionReplay?: {
            plugin: (options: { sampleRate: number }) => any;
        };
    }
}

initializeObservability(store);
initializeSegment();

// Initialize Amplitude with user context once available via Redux.
// We avoid blocking app render; this kicks off script loading immediately.
try {
    const state = store.getState() as any;
    const user = state?.user?.userData?.data;
    const userId = user?.userId as string | undefined;
    initializeAmplitude(userId);
    // Tidio will be initialized only when recommendations are available (in ModularPlansContainer)
    // Re-identify when auth state changes and user becomes available
    let lastUserId = userId;
    store.subscribe(() => {
        try {
            const s = store.getState() as any;
            const u = s?.user?.userData?.data;
            const nextUserId = u?.userId as string | undefined;
            if (nextUserId && nextUserId !== lastUserId) {
                identifyAmplitudeUser(nextUserId);
                lastUserId = nextUserId;
                // Tidio user identification will happen when Tidio is initialized with recommendations
            }
        } catch {
            // no-op
        }
    });
} catch {
    // no-op
}

ReactDOM.render(
    <ReduxProvider store={store}>
        <ThemeProvider theme={Theme}>
            {/** TODO: Show splash page here while data is being loaded **/}
            <PersistGate loading={<Loader />} persistor={persistor}>
                <Router>
                    <RouteTracker />
                    <App />
                </Router>
            </PersistGate>
        </ThemeProvider>
    </ReduxProvider>,
    document.getElementById('root')
);
